import React from 'react';
import { Target, TrendingUp, Gauge } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { HeadlineMeasure } from '../utils/programmeModel';
import ProvenanceBadge from './ProvenanceBadge';
import '../styles/HeadlineMeasures.css';

// HeadlineMeasures — the three headline measures of the value-proven board
// (029-value-proven-dashboard, Increment B; User Story 1, FR-005/FR-006/FR-015/
// FR-016, SC-001/SC-004/SC-008). A PURE view over the model's `measures` array:
// it renders EXACTLY what `deriveValueProven(...).measures` hands it, in the
// north-star order the array gives (progress-to-vision · forecast-accuracy ·
// value-realisation). It does NOT fetch, derive, or read the snapshot — every
// figure is bound verbatim from props.
//
// Honesty contract:
//   • Progress-to-vision (REAL, FR-005/SC-004): a role="progressbar" bound to the
//     measure's `value` (0..100), NEVER its confidence; the figure shows as text
//     too. It carries NO illustrative badge.
//   • Any measure whose provenance is 'illustrative' (forecast-accuracy +
//     value-realisation, FR-006/SC-001) wears the shared ProvenanceBadge — the
//     structural rule, derived from provenance, never hardcoded by id.
//   • Reserved-colour (FR-016/SC-008): NEUTRAL chrome only (index.css tokens);
//     the headline measures NEVER spend the reserved coral (--ap-accent).
//   • a11y (FR-015): each label/state is real text (meaning never by colour
//     alone); the lucide glyphs are decorative (aria-hidden).

const MEASURE_ICON: Record<HeadlineMeasure['id'], LucideIcon> = {
  'progress-to-vision': Target,
  'forecast-accuracy': TrendingUp,
  'value-realisation': Gauge,
};

// The visible figure for a measure. Progress is a clamped whole-number percent;
// the illustrative trend/ratio kinds surface their figure as given.
function figureText(measure: HeadlineMeasure): string {
  if (measure.kind === 'progress') {
    return `${Math.round(measure.value)}%`;
  }
  if (measure.kind === 'ratio') {
    return `${measure.value}×`;
  }
  return `${measure.value}%`;
}

const HeadlineMeasures: React.FC<{ measures: HeadlineMeasure[] }> = ({ measures }) => (
  <div className="ap-headline-measures" role="group" aria-label="Headline measures">
    {measures.map((measure) => {
      const Icon = MEASURE_ICON[measure.id];
      const isProgress = measure.kind === 'progress';
      const isIllustrative = measure.provenance === 'illustrative';
      const value = Math.round(measure.value);
      return (
        <article key={measure.id} className="ap-headline-measure">
          <div className="ap-headline-measure-head">
            <Icon className="ap-headline-measure-icon" size={16} aria-hidden={true} />
            <h3 className="ap-headline-measure-label">{measure.label}</h3>
            {isIllustrative && <ProvenanceBadge source="illustrative" />}
          </div>

          {isProgress ? (
            <div
              className="ap-headline-measure-bar"
              role="progressbar"
              aria-label={measure.label}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={measure.value}
            >
              <span
                className="ap-headline-measure-bar-fill"
                style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
              />
              <span className="ap-headline-measure-figure">{figureText(measure)}</span>
            </div>
          ) : (
            <p className="ap-headline-measure-figure">{figureText(measure)}</p>
          )}
        </article>
      );
    })}
  </div>
);

export default HeadlineMeasures;
